import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { classifyMemberError, MemberErrorKind } from './memberErrors';
import { memberDisplayName } from './memberLabels';
import { ProjectMember } from './types';

const useStyles = makeStyles(theme => ({
  caution: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(2),
  },
}));

type RemoveMemberDialogProps = {
  member: ProjectMember | null;
  onConfirm: (member: ProjectMember) => Promise<void>;
  onClose: () => void;
};

export function RemoveMemberDialog({
  member,
  onConfirm,
  onClose,
}: RemoveMemberDialogProps) {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const [removing, setRemoving] = useState(false);
  const [errorKind, setErrorKind] = useState<MemberErrorKind>();

  useEffect(() => {
    setErrorKind(undefined);
  }, [member]);

  return (
    <Dialog
      open={Boolean(member)}
      onClose={() => {
        if (!removing) onClose();
      }}
    >
      <DialogTitle>{t('members.removeTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          {t('members.removeDescription', {
            name: member
              ? memberDisplayName(member, t('members.selfName'))
              : '',
          })}
        </Typography>
        {errorKind && (
          <Typography variant="body2" className={classes.caution}>
            {t(`members.errors.${errorKind}`)}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={removing} onClick={onClose}>
          {t('actions.cancel')}
        </Button>
        <Button
          color="secondary"
          variant="contained"
          disabled={removing || !member}
          onClick={async () => {
            if (!member) return;
            setRemoving(true);
            setErrorKind(undefined);
            try {
              await onConfirm(member);
              onClose();
            } catch (err) {
              setErrorKind(classifyMemberError(err));
            } finally {
              setRemoving(false);
            }
          }}
        >
          {removing ? t('members.removing') : t('members.confirmRemove')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
